import { useState, useRef } from "react";
import { config } from "../config";

// ─── Webhook constants ────────────────────────────────────────────────────────
const AGENT1_WEBHOOK = `/api/webhook/recipe/${config.m2c.agent1WebhookRecipeId}`;
const AGENT1_TOKEN   = config.m2c.agent1ApiToken;
const AGENT2_WEBHOOK = `/api/webhook/recipe/${config.m2c.agent2WebhookRecipeId}`;
const AGENT2_TOKEN   = config.m2c.agent2ApiToken;
const POLL_URL       = "/api/webhook/request";
const POLL_INTERVAL  = 5000;
const POLL_MAX       = 60;

// ─── Sidebar states ───────────────────────────────────────────────────────────
const S = {
  IDLE:             "idle",
  AGENT1_LOADING:   "agent1_loading",
  AGENT1_DONE:      "agent1_done",
  AGENT1_ERROR:     "agent1_error",
  AGENT2_LOADING:   "agent2_loading",
  AGENT2_NO_REPLY:  "agent2_no_reply",
  AGENT2_PREVIEW:   "agent2_preview",
  AGENT2_ERROR:     "agent2_error",
  AGENT2_APPROVING: "agent2_approving",
  AGENT2_RESOLVED:  "agent2_resolved",
  AGENT2_REJECTED:  "agent2_rejected",
};

// ─── Webhook trigger + poll ───────────────────────────────────────────────────
// Returns void on success. Callers fetch from DB after this resolves.
async function triggerAndPoll(webhookUrl, token, inputMessage) {
  const triggerRes = await fetch(webhookUrl, {
    method:  "POST",
    headers: { "Content-Type": "application/json", "x-api-token": token },
    body:    JSON.stringify({ files: [], input_message: inputMessage, metadata: {} }),
  });
  if (!triggerRes.ok) throw new Error(`Webhook trigger failed: ${triggerRes.status}`);
  const { request_id } = await triggerRes.json();
  if (!request_id) throw new Error("No request_id in webhook response");

  for (let i = 0; i < POLL_MAX; i++) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL));
    const pollRes = await fetch(`${POLL_URL}/${request_id}`, {
      headers: { "x-api-token": token },
    });
    if (!pollRes.ok) continue;
    const data = await pollRes.json();
    if (data?.status === "failed") throw new Error(data?.response || "Agent failed");
    if (data?.status === "success") return;
  }
  throw new Error("Agent timed out after 5 minutes.");
}

// ─── Fetch single case row from Lambda GET ────────────────────────────────────
// lambdaUrl is the same URL meter-2-cash.js already uses — passed as prop/param.
async function fetchCaseFromDB(caseId, lambdaUrl) {
  const res = await fetch(`${lambdaUrl}?case_id=${encodeURIComponent(caseId)}`);
  if (!res.ok) throw new Error(`Lambda fetch failed: ${res.status}`);
  const data = await res.json();
  const row = data?.records?.[0];
  if (!row) throw new Error(`Case ${caseId} not found in DB after agent run`);
  return row;
}

// ─── Map DB row → Agent 1 UI state ───────────────────────────────────────────
// Exported so meter-2-cash.js can restore sidebar state on panel open / page refresh.
export function mapDbToAgent1State(row) {
  return {
    exception_information: {
      case_id:        row.case_id,
      account_id:     row.account_id,
      exception_type: row.case_type,
      scenario:       row.scenario_detected,
      customer_name:  row.customer_name || null,
      property_type:  row.property_type || null,
      status:         row.status,
      overdue:        row.overdue || false,
    },
    // Populated after Agent 1 prompt update. Null for old rows → Timeline shows fallback.
    actions_performed: row.agent1_actions_performed || null,

    // email_content[0] after prompt update: { to, body_preview, sent_at, subject, processing_id }
    // Old rows: body_preview is null, EmailCard renders nothing for it — graceful degradation.
    email_notification: row.email_content?.[0]
      ? {
          sent_to:       row.email_content[0].to,
          subject:       row.email_content[0].subject,
          body_preview:  row.email_content[0].body_preview  || null,
          sent_at:       row.email_content[0].sent_at,
          processing_id: row.email_content[0].processing_id,
        }
      : null,

    next_steps_and_deadlines: row.next_steps_and_deadlines || null,
  };
}

// ─── Map DB row → Agent 2 UI state ───────────────────────────────────────────
// Exported so meter-2-cash.js can restore Agent 2 preview/resolved/rejected state.
export function mapDbToAgent2State(row) {
  return {
    // agent2_mode is written to DB even in PREVIEW mode — drives routing in runAgent2Scan
    mode:                  row.agent2_mode              || null,
    resolution_outcome:    row.resolution_outcome       || null,
    reply_analysis:        row.reply_analysis           || null,
    sop_procedure_applied: row.sop_procedure_applied    || null,
    actions_completed:     row.agent2_actions_completed || null,
    exception_details: {
      case_id:                row.case_id,
      exception_type:         row.case_type,
      scenario:               row.scenario_detected,
      awaiting_response_from: row.awaiting_response_from,
      original_deadline:      row.response_deadline,
    },
  };
}

// ─── Exported helper — called by meter-2-cash.js handleResolve ───────────────
// Pass the same `url` constant meter-2-cash.js already has as lambdaUrl.
export async function triggerAgent1(caseId, lambdaUrl) {
  await triggerAndPoll(AGENT1_WEBHOOK, AGENT1_TOKEN, `Process exception case ${caseId}`);
  const row = await fetchCaseFromDB(caseId, lambdaUrl);
  return mapDbToAgent1State(row);
}

// ─── Icons ────────────────────────────────────────────────────────────────────
// (removed)

// ─── Strip em dashes from DB text ─────────────────────────────────────────────
function clean(text) {
  if (!text) return text;
  return String(text).replace(/\s*—\s*/g, " ").replace(/—/g, " ");
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spin({ size = "md" }) {
  const sz = size === "sm" ? "w-4 h-4" : size === "lg" ? "w-8 h-8" : "w-5 h-5";
  return (
    <svg className={`${sz} animate-spin text-[#0070F2]`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
    </svg>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ value }) {
  const upper = (value || "").toUpperCase().replace(/ /g, "_");
  const map = {
    RESOLVED:         "bg-emerald-50 text-emerald-700 border-emerald-200",
    IN_PROGRESS:      "bg-blue-50 text-blue-700 border-blue-200",
    ESCALATED:        "bg-red-50 text-red-700 border-red-200",
    OPEN:             "bg-amber-50 text-amber-700 border-amber-200",
    REJECTED:         "bg-red-100 text-red-800 border-red-300",
    ON_HOLD:          "bg-slate-50 text-slate-700 border-slate-300",
    PENDING_APPROVAL: "bg-violet-50 text-violet-700 border-violet-200",
  };
  const dotMap = {
    RESOLVED: "bg-emerald-500", IN_PROGRESS: "bg-blue-500", ESCALATED: "bg-red-500",
    OPEN: "bg-amber-500", REJECTED: "bg-red-700", ON_HOLD: "bg-slate-400",
    PENDING_APPROVAL: "bg-violet-500",
  };
  const cls = map[upper] || "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dotMap[upper] || "bg-gray-400"}`} />
      {upper.replace(/_/g, " ")}
    </span>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────
function SectionCard({ accent = "blue", title, children }) {
  const headers = {
    blue:   "bg-[#003399]",
    green:  "bg-gradient-to-r from-emerald-600 to-emerald-500",
    teal:   "bg-gradient-to-r from-teal-700 to-teal-500",
    purple: "bg-gradient-to-r from-violet-700 to-violet-500",
    orange: "bg-gradient-to-r from-orange-600 to-orange-400",
    slate:  "bg-gradient-to-r from-slate-700 to-slate-500",
    red:    "bg-gradient-to-r from-red-700 to-red-500",
  };
  const borders = {
    blue:   "border-[#B8D6FF]", green:  "border-emerald-200", teal:   "border-teal-200",
    purple: "border-violet-200", orange: "border-orange-200", slate:  "border-slate-200",
    red:    "border-red-200",
  };
  return (
    <div className={`rounded-2xl border ${borders[accent] || borders.blue} shadow-sm overflow-hidden bg-white`}>
      <div className={`${headers[accent] || headers.blue} px-5 py-3 flex items-center gap-2.5`}>
        <h3 className="text-sm font-bold uppercase tracking-widest text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// ─── KV pair ──────────────────────────────────────────────────────────────────
function KV({ label, value, mono = false }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-0.5">{label}</p>
      <p className={`text-sm text-[#1D2D3E] ${mono ? "Inter" : ""}`}>{clean(value)}</p>
    </div>
  );
}

// ─── Timeline ─────────────────────────────────────────────────────────────────
function Timeline({ steps, accent = "blue" }) {
  if (!steps?.length) return (
    <p className="text-sm text-[#556B82] italic px-5 py-4">No actions recorded yet.</p>
  );
  const dotBg = accent === "teal" ? "bg-teal-500 ring-teal-100" : "bg-[#0070F2] ring-[#B8D6FF]";
  return (
    <div className="px-5 py-5">
      {steps.map((s, idx) => {
        const isPending = (s.status || "").toLowerCase() === "pending";
        const isLast    = idx === steps.length - 1;
        return (
          <div key={idx} className="flex gap-4 items-stretch">
            <div className="flex flex-col items-center flex-shrink-0 w-8">
              <div className="w-0.5 bg-[#B8D6FF] flex-1" />
              <div className={`w-8 h-8 rounded-full ${isPending ? "bg-amber-400 ring-amber-100" : dotBg} ring-4 flex items-center justify-center shadow-sm flex-shrink-0`}>
                <span className="text-white text-xs font-bold">{s.step || idx + 1}</span>
              </div>
              {isLast ? <div className="flex-1" /> : <div className="w-0.5 bg-[#B8D6FF] flex-1" />}
            </div>
            <div className="flex-1 py-2 pb-5">
              <div className="bg-[#EBF4FF] border border-[#B8D6FF] rounded-xl p-4 shadow-sm">
                <p className="text-base font-bold text-[#0040B0] uppercase tracking-wide mb-1">
                  {isPending ? "Pending" : "Completed"}
                </p>
                <p className="text-sm text-[#1D2D3E] leading-relaxed">{clean(s.description)}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Exception info ───────────────────────────────────────────────────────────
function ExceptionInfo({ info, rowData }) {
  const d = info    || {};
  const r = rowData || {};
  return (
    <div className="p-5 grid grid-cols-2 gap-x-6 gap-y-4">
      <KV label="Case ID"        value={d.case_id        || r.case_id}        mono />
      <KV label="Account"        value={d.account_id     || r.account_id}     mono />
      <KV label="Customer"       value={d.customer_name  || r.customer_name} />
      <KV label="Exception Type" value={d.exception_type || r.case_type} />
      <KV label="Scenario"       value={d.scenario       || r.scenario_detected} />
      <KV label="Property Type"  value={d.property_type  || r.property_type} />
      {(d.status || r.status) && (
        <div className="col-span-2 pt-2 border-t border-[#EBF4FF]">
          <StatusBadge value={d.status || r.status} />
        </div>
      )}
    </div>
  );
}

// ─── Email card ───────────────────────────────────────────────────────────────
function EmailCard({ email }) {
  if (!email) return (
    <p className="text-sm text-[#556B82] italic px-5 py-4">No notification data available.</p>
  );
  return (
    <div className="p-5 space-y-3">
      {email.sent_to && (
        <div className="flex items-center gap-2">
          <span className="text-base font-bold text-[#556B82] uppercase tracking-widest">To:</span>
          <span className="text-sm font-semibold text-[#1D2D3E]">{clean(email.sent_to)}</span>
        </div>
      )}
      {email.subject && (
        <div>
          <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-0.5">Subject</p>
          <p className="text-sm font-semibold text-[#1D2D3E]">{clean(email.subject)}</p>
        </div>
      )}
      {/* body_preview populated after Agent 1 prompt update. Null for old rows → renders nothing. */}
      {email.body_preview && (
        <div className="bg-[#F8FBFF] border border-[#B8D6FF] rounded-xl p-4 text-sm text-[#1D2D3E] leading-relaxed whitespace-pre-wrap max-h-56 overflow-y-auto">
          {clean(email.body_preview)}
        </div>
      )}
      {email.sent_at && (
        <p className="text-xs text-[#556B82]">Sent: {new Date(email.sent_at).toLocaleString()}</p>
      )}
    </div>
  );
}

// ─── Next steps ───────────────────────────────────────────────────────────────
function NextSteps({ data }) {
  if (!data) return null;
  const { next_steps = [], deadlines = [], escalation_triggers = [] } = data;
  if (!next_steps.length && !deadlines.length && !escalation_triggers.length) return null;
  return (
    <div className="p-5 space-y-4">
      {next_steps.length > 0 && (
        <div>
          <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-2">Next Steps</p>
          <ul className="space-y-1.5">{next_steps.map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-orange-700 bg-orange-50 rounded-lg px-3 py-2 border border-orange-200">
              <span className="mt-0.5">{clean(s)}</span>
            </li>
          ))}</ul>
        </div>
      )}
      {deadlines.length > 0 && (
        <div>
          <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-2">Deadlines</p>
          <ul className="space-y-1.5">{deadlines.map((d, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-orange-700 bg-orange-50 rounded-lg px-3 py-2 border border-orange-200">
              {clean(d)}
            </li>
          ))}</ul>
        </div>
      )}
      {escalation_triggers.length > 0 && (
        <div>
          <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-2">Escalation Triggers</p>
          <ul className="space-y-1.5">{escalation_triggers.map((t, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2 border border-red-200">
              {clean(t)}
            </li>
          ))}</ul>
        </div>
      )}
    </div>
  );
}

// ─── Agent 2 preview card ─────────────────────────────────────────────────────
function Agent2PreviewCard({ data }) {
  const outcome = data?.resolution_outcome    || {};
  const excDet  = data?.exception_details     || {};
  const reply   = data?.reply_analysis        || {};
  const sop     = data?.sop_procedure_applied || {};
  return (
    <div className="p-5 space-y-5">
      <div className="flex items-center gap-3 flex-wrap bg-[#EBF4FF] rounded-xl px-4 py-3 border border-[#B8D6FF]">
        <StatusBadge value={outcome.status || "PENDING_APPROVAL"} />
        {outcome.reason && (
          <span className="text-sm text-[#1D2D3E] flex-1 font-medium">{clean(outcome.reason)}</span>
        )}
      </div>

      {(excDet.case_id || excDet.exception_type) && (
        <div>
          <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-2">Exception</p>
          <div className="bg-[#F8FBFF] border border-[#B8D6FF] rounded-xl p-4 text-sm space-y-2">
            {excDet.case_id                && <p className="text-[#1D2D3E]"><span className="font-bold text-[#0040B0]">Case: </span>{excDet.case_id}</p>}
            {excDet.exception_type         && <p className="text-[#1D2D3E]"><span className="font-bold text-[#0040B0]">Type: </span>{clean(excDet.exception_type)}</p>}
            {excDet.scenario               && <p className="text-[#1D2D3E]"><span className="font-bold text-[#0040B0]">Scenario: </span>{clean(excDet.scenario)}</p>}
            {excDet.awaiting_response_from && <p className="text-[#1D2D3E]"><span className="font-bold text-[#0040B0]">Replied by: </span>{clean(excDet.awaiting_response_from)}</p>}
            {excDet.original_deadline      && <p className="text-[#1D2D3E]"><span className="font-bold text-[#0040B0]">Original deadline: </span>{clean(excDet.original_deadline)}</p>}
          </div>
        </div>
      )}

      {reply.summary && (
        <div>
          <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-2">Reply Analysis</p>
          <div className="bg-[#F8FBFF] border border-[#B8D6FF] rounded-xl p-4 text-md space-y-2">
            <p className="text-[#1D2D3E]"><span className="font-bold text-[#0040B0]">Summary: </span>{clean(reply.summary)}</p>
            <p className="text-[#1D2D3E]">
              <span className="font-bold text-[#0040B0]">Satisfies SOP: </span>
              {reply.satisfies_sop_criteria === true || reply.satisfies_sop_criteria === "true" ? "Yes" : "No"}
            </p>
            {reply.reason && (
              <p className="text-[#1D2D3E]"><span className="font-bold text-[#0040B0]">Reason: </span>{clean(reply.reason)}</p>
            )}
          </div>
        </div>
      )}

      {sop.sop_reference && (
        <div>
          <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-2">SOP Applied</p>
          <div className="bg-[#F8FBFF] border border-[#B8D6FF] rounded-xl p-4 text-md space-y-2">
            <p className="text-[#1D2D3E]"><span className="font-bold text-[#0040B0]">Reference: </span>{clean(sop.sop_reference)}</p>
            {sop.resolution_criteria_checked && (
              <p className="text-[#1D2D3E]"><span className="font-bold text-[#0040B0]">Criteria: </span>{clean(sop.resolution_criteria_checked)}</p>
            )}
            {sop.outcome && (
              <p className="text-[#1D2D3E]"><span className="font-bold text-[#0040B0]">Outcome: </span><span className="capitalize font-semibold">{clean(sop.outcome)}</span></p>
            )}
          </div>
        </div>
      )}

      {Array.isArray(data?.actions_completed) && data.actions_completed.length > 0 && (
        <div>
          <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-2">Steps Completed</p>
          <Timeline steps={data.actions_completed} accent="teal" />
        </div>
      )}
    </div>
  );
}

// ─── Loading / Error ──────────────────────────────────────────────────────────
function LoadingScreen({ message }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[320px] gap-4 p-8">
      <Spin size="lg" />
      <p className="text-base font-semibold text-[#1D2D3E] text-center">{message}</p>
      <p className="text-sm text-[#556B82] text-center">This may take up to 5 minutes</p>
      <div className="flex gap-1.5 mt-2">
        {[0,1,2].map((i) => (
          <div key={i} className="w-2 h-2 rounded-full bg-[#0070F2] animate-bounce"
               style={{ animationDelay: `${i * 0.15}s` }} />
        ))}
      </div>
    </div>
  );
}

function ErrorScreen({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[240px] gap-4 p-8">
      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
        <span className="text-red-600 text-xl font-bold">X</span>
      </div>
      <p className="text-base font-semibold text-[#1D2D3E] text-center">{message}</p>
      {onRetry && (
        <button type="button" onClick={onRetry}
          className="px-5 py-2 text-sm font-bold text-white bg-[#003399] rounded-lg hover:bg-[#002277] transition-all shadow-sm">
          Retry
        </button>
      )}
    </div>
  );
}

// ─── ResolveSidebar ───────────────────────────────────────────────────────────
export default function ResolveSidebar({
  lambdaUrl,
  rowData, isOpen, onClose, onStatusChange,
  phase, setPhase, agent1, setAgent1, agent2, setAgent2,
  activeCaseId,
}) {
  const row    = rowData || {};
  const caseId = activeCaseId || row.case_id || "";

  const [errorMsg, setErrorMsg] = useState("");
  const pollingRef = useRef(false);

  // ── Agent 2 — check for reply (PREVIEW) ──────────────────────────────────
  const runAgent2Scan = async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setPhase(S.AGENT2_LOADING);
    setErrorMsg("");
    try {
      await triggerAndPoll(AGENT2_WEBHOOK, AGENT2_TOKEN, `Check responses for case ${caseId}`);
      const dbRow = await fetchCaseFromDB(caseId, lambdaUrl);
      const mapped = mapDbToAgent2State(dbRow);
      setAgent2(mapped);
      // agent2_mode written to DB even in PREVIEW — drives routing
      const mode = (mapped.mode || "").toLowerCase();
      if (mode === "no_reply") {
        setPhase(S.AGENT2_NO_REPLY);
      } else {
        setPhase(S.AGENT2_PREVIEW);
      }
    } catch (err) {
      setErrorMsg(err.message || "Agent 2 failed");
      setPhase(S.AGENT2_ERROR);
    } finally { pollingRef.current = false; }
  };

  // ── Approve ───────────────────────────────────────────────────────────────
  const handleApprove = async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setPhase(S.AGENT2_APPROVING);
    setErrorMsg("");
    try {
      await triggerAndPoll(AGENT2_WEBHOOK, AGENT2_TOKEN, `APPROVE resolution for case ${caseId}`);
      const dbRow = await fetchCaseFromDB(caseId, lambdaUrl);
      setAgent2(mapDbToAgent2State(dbRow));
      setPhase(S.AGENT2_RESOLVED);
      if (onStatusChange) onStatusChange(caseId, "RESOLVED");
    } catch (err) {
      setErrorMsg(err.message || "Approval failed");
      setPhase(S.AGENT2_ERROR);
    } finally { pollingRef.current = false; }
  };

  // ── Reject ────────────────────────────────────────────────────────────────
  const handleReject = async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setPhase(S.AGENT2_APPROVING);
    setErrorMsg("");
    try {
      await triggerAndPoll(AGENT2_WEBHOOK, AGENT2_TOKEN, `REJECT resolution for case ${caseId}`);
      const dbRow = await fetchCaseFromDB(caseId, lambdaUrl);
      setAgent2(mapDbToAgent2State(dbRow));
      setPhase(S.AGENT2_REJECTED);
      if (onStatusChange) onStatusChange(caseId, "REJECTED");
    } catch (err) {
      setErrorMsg(err.message || "Rejection failed. DB status may not have saved");
      setPhase(S.AGENT2_REJECTED);
      if (onStatusChange) onStatusChange(caseId, "REJECTED");
    } finally { pollingRef.current = false; }
  };

  const invoiceDisplay = agent1?.exception_information?.case_id || caseId || "N/A";

  return (
    <div className="fixed inset-0 z-50 flex pointer-events-none">
      <div className="flex-1" />
      <div className={`w-full max-w-3xl bg-[#F4F8FF] shadow-2xl flex flex-col h-full overflow-hidden border-l border-[#B8D6FF] pointer-events-auto transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}>

        {/* Header */}
        <div className="bg-[#003399] px-6 py-4 flex items-center justify-between gap-4 flex-shrink-0">
          <h2 className="text-lg font-bold text-white leading-tight">Exception Resolution</h2>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white text-2xl leading-none w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors flex-shrink-0" aria-label="Close">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {!caseId && (
            <div className="flex flex-col items-center justify-center min-h-[320px] gap-4 p-8 text-center">
              <p className="text-base font-semibold text-[#1D2D3E]">No case selected</p>
              <p className="text-sm text-[#556B82]">Click the panel icon on any row to view details,<br/>or click Resolve to trigger Agent 1.</p>
            </div>
          )}

          {caseId && phase === S.IDLE && (
            <SectionCard accent="blue" title="Exception Details">
              <ExceptionInfo info={null} rowData={row} />
            </SectionCard>
          )}

          {phase === S.AGENT1_LOADING && (
            <LoadingScreen message="Agent 1 is analyzing the exception and preparing notifications…" />
          )}

          {phase === S.AGENT1_ERROR && (
            <ErrorScreen message={errorMsg || "Agent 1 failed to complete."} />
          )}

          {[S.AGENT1_DONE, S.AGENT2_LOADING, S.AGENT2_NO_REPLY, S.AGENT2_PREVIEW,
            S.AGENT2_APPROVING, S.AGENT2_RESOLVED, S.AGENT2_REJECTED, S.AGENT2_ERROR
          ].includes(phase) && (
            <>
              <SectionCard accent="blue" title="Exception Details">
                <ExceptionInfo info={agent1?.exception_information} rowData={row} />
              </SectionCard>

              <SectionCard accent="blue" title="Agent 1 Actions Performed">
                <Timeline steps={agent1?.actions_performed} accent="blue" />
              </SectionCard>

              <SectionCard accent="slate" title="Email Sent to Ops Team">
                <EmailCard email={agent1?.email_notification} />
              </SectionCard>

              {agent1?.next_steps_and_deadlines && (
                <SectionCard accent="orange" title="Next Steps & Deadlines">
                  <NextSteps data={agent1.next_steps_and_deadlines} />
                </SectionCard>
              )}
            </>
          )}

          {phase === S.AGENT2_LOADING && (
            <LoadingScreen message="Agent 2 is scanning for replies from the ops team…" />
          )}

          {phase === S.AGENT2_APPROVING && (
            <LoadingScreen message="Processing your decision and updating the database…" />
          )}

          {phase === S.AGENT2_ERROR && (
            <ErrorScreen message={errorMsg || "Agent 2 failed."} onRetry={runAgent2Scan} />
          )}

          {phase === S.AGENT2_NO_REPLY && (
            <SectionCard accent="slate" title="Agent 2 No Reply Found">
              <div className="p-5 space-y-4">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start gap-3">
                  <div>
                    <p className="text-base font-semibold text-[#1D2D3E] mb-1">No reply received yet</p>
                    <p className="text-sm text-[#556B82]">
                      {clean(agent2?.resolution_outcome?.reason || "No email reply found for this case. The response deadline has not yet passed.")}
                    </p>
                  </div>
                </div>
                {Array.isArray(agent2?.actions_completed) && agent2.actions_completed.length > 0 && (
                  <div>
                    <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-2">What Agent 2 Checked</p>
                    <Timeline steps={agent2.actions_completed} accent="teal" />
                  </div>
                )}
              </div>
            </SectionCard>
          )}

          {phase === S.AGENT2_PREVIEW && agent2 && (
            <SectionCard accent="purple" title="Agent 2 Resolution Preview">
              <Agent2PreviewCard data={agent2} />
            </SectionCard>
          )}

          {phase === S.AGENT2_RESOLVED && agent2 && (
            <SectionCard accent="green" title="Agent 2 Case Resolved">
              <Agent2PreviewCard data={{
                ...agent2,
                resolution_outcome: { ...agent2.resolution_outcome, status: "RESOLVED" }
              }} />
            </SectionCard>
          )}

          {phase === S.AGENT2_REJECTED && (
            <SectionCard accent="red" title="Case Rejected">
              <div className="p-5">
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                  <p className="text-base font-bold text-red-800 mb-1">Case Rejected</p>
                  <p className="text-sm text-red-600">This exception has been marked as rejected and the DB has been updated.</p>
                  {errorMsg && (
                    <p className="text-xs text-red-500 mt-2 bg-red-100 rounded-lg px-3 py-2">{errorMsg}</p>
                  )}
                </div>
                {agent2?.actions_completed?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-base font-bold text-[#556B82] uppercase tracking-widest mb-2">Steps Completed</p>
                    <Timeline steps={agent2.actions_completed} accent="teal" />
                  </div>
                )}
              </div>
            </SectionCard>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[#B8D6FF] bg-white flex items-center justify-between gap-3 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[#556B82] border border-[#B8D6FF] rounded-lg hover:bg-[#EBF4FF] transition-colors">
            Close
          </button>
          <div className="flex items-center gap-2">
            {(phase === S.AGENT1_DONE || phase === S.AGENT2_NO_REPLY) && (
              <button type="button" onClick={runAgent2Scan}
                className="px-5 py-2 text-sm font-bold text-white bg-[#003399] rounded-lg hover:bg-[#002277] transition-all shadow-sm flex items-center gap-2">
                <span>{phase === S.AGENT2_NO_REPLY ? "Check Again" : "Check for Reply"}</span>
              </button>
            )}
            {phase === S.AGENT2_PREVIEW && (
              <>
                <button type="button" onClick={handleReject}
                  className="px-4 py-2 text-sm font-bold text-red-700 border border-red-300 bg-red-50 rounded-lg hover:bg-red-100 transition-all">
                  Reject
                </button>
                <button type="button" onClick={handleApprove}
                  className="px-5 py-2 text-sm font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-all shadow-sm flex items-center gap-2">
                  Approve
                </button>
              </>
            )}
            {phase === S.AGENT2_RESOLVED && <StatusBadge value="RESOLVED" />}
            {phase === S.AGENT2_REJECTED  && <StatusBadge value="REJECTED" />}
          </div>
        </div>

      </div>
    </div>
  );
}